import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  ArrowLeft,
  FileText,
  CheckCircle,
  Users,
  Copy,
  Download,
  BarChart3,
  X,
  SmilePlus,
  TrendingUp,
  Star,
  Send,
  Lightbulb,
  ChevronDown,
  Printer,
  Trash2,
  HelpCircle,
  List,
  Hash,
  Check,
} from "lucide-react";
import apiService from "../services/apiService";
import { useAuth } from "../contexts/AuthContext";

const SummarySection = ({ id, icon: Icon, title, badge, onCopy, children }) => (
  <section
    id={id}
    className="bg-white border border-gray-200 rounded-xl px-5 py-4 scroll-mt-4"
  >
    <div className="flex items-center justify-between pb-3 mb-3 border-b border-gray-100">
      <h2 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
        {Icon && <Icon size={16} className="text-gray-700" />}
        {title}
        {badge != null && (
          <span className="text-xs font-normal text-gray-500">({badge})</span>
        )}
      </h2>
      {onCopy && (
        <button
          type="button"
          onClick={onCopy}
          className="p-1 text-gray-400 hover:text-gray-700 transition-colors"
          title="Copy"
          aria-label="Copy"
        >
          <Copy size={14} />
        </button>
      )}
    </div>
    <div className="text-sm">{children}</div>
  </section>
);

const TocLink = ({ href, icon: Icon, label, count }) => (
  <li>
    <a
      href={`#${href}`}
      className="flex items-center gap-2 px-2 py-1.5 rounded text-gray-700 hover:bg-gray-100 transition-colors"
    >
      <Icon size={14} className="text-gray-500 flex-shrink-0" />
      <span className="flex-1 truncate">{label}</span>
      {count != null && (
        <span className="text-xs text-gray-500">{count}</span>
      )}
    </a>
  </li>
);

const MeetingSummaryPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { meetingId } = useParams();
  const { user } = useAuth();

  // Navigation state from the host's stopCapture or participant's meeting_end handler.
  // May be absent (participant fallback navigation, page refresh). When absent we fetch
  // from the backend so participants always see the actual summary data.
  const [summaryData, setSummaryData] = useState(location.state?.summaryData || null);
  const [loadingSummary, setLoadingSummary] = useState(!location.state?.summaryData);

  useEffect(() => {
    if (location.state?.summaryData || !meetingId) return;
    let cancelled = false;
    const fetchFromBackend = async () => {
      try {
        const data = await apiService.getMeetingSummary(meetingId);
        if (!cancelled && data?.status === "success") {
          setSummaryData(data);
        }
      } catch (err) {
        console.error("[MeetingSummaryPage] Failed to fetch summary:", err);
      } finally {
        if (!cancelled) setLoadingSummary(false);
      }
    };
    fetchFromBackend();
    return () => { cancelled = true; };
  }, [meetingId, location.state?.summaryData]);

  const [showEvaluationModal, setShowEvaluationModal] = useState(false);
  const [evaluationData, setEvaluationData] = useState(null);
  const [evaluationType, setEvaluationType] = useState("sales");
  const [loadingEvaluation, setLoadingEvaluation] = useState(false);
  const [evaluationError, setEvaluationError] = useState(null);

  const [showSentimentModal, setShowSentimentModal] = useState(false);
  const [sentimentData, setSentimentData] = useState(null);
  const [loadingSentiment, setLoadingSentiment] = useState(false);
  const [sentimentError, setSentimentError] = useState(null);

  // Suggestions state
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [feedbackStates, setFeedbackStates] = useState({});
  const [submittingFeedback, setSubmittingFeedback] = useState({});

  // Follow-up questions state
  const [followUpQuestions, setFollowUpQuestions] = useState([]);
  const [loadingFollowUpQuestions, setLoadingFollowUpQuestions] = useState(false);

  // UI state
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);
  const [completedActions, setCompletedActions] = useState(() => {
    try {
      const raw = window.localStorage.getItem(`meeting_${meetingId}_done`);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch {
      return new Set();
    }
  });
  const [dismissedSuggestions, setDismissedSuggestions] = useState(new Set());

  useEffect(() => {
    try {
      window.localStorage.setItem(
        `meeting_${meetingId}_done`,
        JSON.stringify(Array.from(completedActions))
      );
    } catch {
      /* ignore */
    }
  }, [completedActions, meetingId]);

  useEffect(() => {
    const handler = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Fetch suggestions and follow-up questions on component mount
  useEffect(() => {
    if (meetingId) {
      fetchSuggestions();
      fetchFollowUpQuestions();
    }
  }, [meetingId]);

  // Fetch suggestions for the meeting
  const fetchSuggestions = async () => {
    setLoadingSuggestions(true);
    try {
      const response = await apiService.get(
        `/feedback/meeting/${meetingId}/suggestions`
      );
      if (response.success) {
        setSuggestions(response.suggestions || []);
      }
    } catch (error) {
      console.error("Error fetching suggestions:", error);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  // Fetch follow-up questions for the meeting
  const fetchFollowUpQuestions = async () => {
    setLoadingFollowUpQuestions(true);
    try {
      const response = await apiService.get(
        `/feedback/meeting/${meetingId}/followup-questions`
      );
      if (response.success) {
        setFollowUpQuestions(response.questions || []);
      }
    } catch (error) {
      console.error("Error fetching follow-up questions:", error);
    } finally {
      setLoadingFollowUpQuestions(false);
    }
  };

  // Submit feedback for a suggestion
  const submitFeedback = async (suggestionId, rating, feedbackText) => {
    try {
      setSubmittingFeedback((prev) => ({ ...prev, [suggestionId]: true }));

      const data = await apiService.submitSuggestionFeedback(
        suggestionId,
        rating,
        feedbackText
      );

      // Update feedback state
      setFeedbackStates((prev) => ({
        ...prev,
        [suggestionId]: {
          ...prev[suggestionId],
          submitted: true,
        },
      }));

      // Update the suggestion in the list to reflect the feedback
      setSuggestions((prev) =>
        prev.map((s) =>
          s.id === suggestionId
            ? {
                ...s,
                rating,
                feedback: feedbackText,
                feedback_timestamp: new Date().toISOString(),
              }
            : s
        )
      );

      // Hide feedback box after 2 seconds
      setTimeout(() => {
        setFeedbackStates((prev) => ({
          ...prev,
          [suggestionId]: {
            ...prev[suggestionId],
            showFeedbackBox: false,
          },
        }));
      }, 2000);
    } catch (error) {
      console.error("❌ Error submitting feedback:", error);
      alert("Failed to submit feedback. Please try again.");
    } finally {
      setSubmittingFeedback((prev) => ({ ...prev, [suggestionId]: false }));
    }
  };

  // Handle star rating click
  const handleStarClick = (suggestionId, rating) => {
    setFeedbackStates((prev) => ({
      ...prev,
      [suggestionId]: {
        ...prev[suggestionId],
        rating,
        showFeedbackBox: true,
      },
    }));
  };

  // Handle feedback text change
  const handleFeedbackChange = (suggestionId, text) => {
    setFeedbackStates((prev) => ({
      ...prev,
      [suggestionId]: {
        ...prev[suggestionId],
        feedback: text,
      },
    }));
  };

  // Fetch evaluation data
  const fetchEvaluation = async () => {
    // Get user ID - try multiple properties that MSAL account might have
    const userId =
      user?.localAccountId || user?.homeAccountId || user?.username || user?.id;

    if (!meetingId) {
      setEvaluationError(
        "Meeting ID not available. Please navigate from a meeting page."
      );
      return;
    }

    if (!userId) {
      setEvaluationError("User ID not available. Please try logging in again.");
      return;
    }

    setLoadingEvaluation(true);
    setEvaluationError(null);

    try {
      const response = await apiService.get(
        `/meetings/evaluation?user_id=${userId}&meeting_id=${meetingId}`
      );

      // Extract the evaluation data from the response
      const evaluationData = {
        sales_evaluation: response.sales_evaluation || {},
        client_evaluation: response.client_evaluation || {},
      };

      setEvaluationData(evaluationData);
      setShowEvaluationModal(true);
    } catch (error) {
      setEvaluationError(error.message || "Failed to fetch evaluation data");
    } finally {
      setLoadingEvaluation(false);
    }
  };

  // Fetch sentiment data
  const fetchSentiment = async () => {
    const userId =
      user?.localAccountId || user?.homeAccountId || user?.username || user?.id;

    if (!meetingId) {
      setSentimentError(
        "Meeting ID not available. Please navigate from a meeting page."
      );
      return;
    }

    if (!userId) {
      setSentimentError("User ID not available. Please try logging in again.");
      return;
    }

    setLoadingSentiment(true);
    setSentimentError(null);

    try {
      const response = await apiService.get(
        `/meetings/sentiment?user_id=${userId}&meeting_id=${meetingId}`
      );

      // Extract sentiment data from response (it's nested under "sentiment" key)
      const sentimentData = response.sentiment || response;

      setSentimentData(sentimentData);
      setShowSentimentModal(true);
    } catch (error) {
      setSentimentError(error.message || "Failed to fetch sentiment data");
    } finally {
      setLoadingSentiment(false);
    }
  };

  // Helper to safely get numeric value
  const getNumericValue = (value) => {
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  };

  const renderScoreBar = (score, maxScore = 10) => {
    const numericScore = getNumericValue(score);
    const percentage = (numericScore / maxScore) * 100;

    // Gradient color based on percentage for dark theme
    let gradientColor;
    if (percentage >= 70) {
      gradientColor = "linear-gradient(90deg, #10b981 0%, #34d399 100%)"; // Green gradient
    } else if (percentage >= 40) {
      gradientColor = "linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%)"; // Blue gradient
    } else {
      gradientColor = "linear-gradient(90deg, #8b5cf6 0%, #a78bfa 100%)"; // Purple gradient
    }

    return (
      <div style={{ width: "100%", marginTop: "8px" }}>
        <div
          style={{
            height: "10px",
            backgroundColor: "rgba(51, 65, 85, 0.5)", // Dark slate background
            borderRadius: "6px",
            overflow: "hidden",
            border: "1px solid rgba(100, 116, 139, 0.3)",
          }}
        >
          <div
            style={{
              width: `${percentage}%`,
              height: "100%",
              background: gradientColor,
              transition: "width 0.3s ease",
              boxShadow: "0 0 10px rgba(59, 130, 246, 0.3)",
            }}
          />
        </div>
        <span
          style={{
            fontSize: "13px",
            color: "rgb(148, 163, 184)",
            marginTop: "6px",
            display: "block",
            fontWeight: "500",
          }}
        >
          {numericScore} / {maxScore}
        </span>
      </div>
    );
  };

  const renderSalesEvaluation = (data) => {
    if (!data) return null;

    // Helper to check if value is numeric
    const isNumeric = (val) =>
      typeof val === "number" || !isNaN(parseFloat(val));

    return (
      <div className="evaluation-content">
        {/* Overall Performance Score */}
        {data["Overall Salesperson Performance Score"] !== undefined && (
          <div className="eval-section">
            <h3 className="eval-section-title">
              Overall Salesperson Performance Score:{" "}
              {data["Overall Salesperson Performance Score"]}/10
            </h3>
            {renderScoreBar(data["Overall Salesperson Performance Score"], 10)}
          </div>
        )}

        {/* Sales Techniques */}
        {data["Sales Techniques"] && (
          <div className="eval-section">
            <h3 className="eval-section-title">Sales Techniques</h3>
            {Object.entries(data["Sales Techniques"]).map(([key, value]) => (
              <div key={key} className="eval-metric">
                <span className="metric-label">{key}</span>
                {isNumeric(value) ? (
                  renderScoreBar(value, 10)
                ) : (
                  <p
                    style={{
                      marginTop: "5px",
                      color: "#555",
                      fontSize: "14px",
                    }}
                  >
                    {value}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Product Knowledge */}
        {data["Product Knowledge"] && (
          <div className="eval-section">
            <h3 className="eval-section-title">Product Knowledge</h3>
            {Object.entries(data["Product Knowledge"]).map(([key, value]) => (
              <div key={key} className="eval-metric">
                <span className="metric-label">{key}</span>
                {isNumeric(value) ? (
                  renderScoreBar(value, 10)
                ) : (
                  <p
                    style={{
                      marginTop: "5px",
                      color: "#555",
                      fontSize: "14px",
                    }}
                  >
                    {value}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Communication Skills */}
        {data["Communication Skills"] && (
          <div className="eval-section">
            <h3 className="eval-section-title">Communication Skills</h3>
            {Object.entries(data["Communication Skills"]).map(
              ([key, value]) => (
                <div key={key} className="eval-metric">
                  <span className="metric-label">{key}</span>
                  {isNumeric(value) ? (
                    renderScoreBar(value, 10)
                  ) : (
                    <p
                      style={{
                        marginTop: "5px",
                        color: "#555",
                        fontSize: "14px",
                      }}
                    >
                      {value}
                    </p>
                  )}
                </div>
              )
            )}
          </div>
        )}

        {/* Meeting Management */}
        {data["Meeting Management"] && (
          <div className="eval-section">
            <h3 className="eval-section-title">Meeting Management</h3>
            {Object.entries(data["Meeting Management"]).map(([key, value]) => (
              <div key={key} className="eval-metric">
                <span className="metric-label">{key}</span>
                {isNumeric(value) ? (
                  renderScoreBar(value, 10)
                ) : (
                  <p
                    style={{
                      marginTop: "5px",
                      color: "#555",
                      fontSize: "14px",
                    }}
                  >
                    {value}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Relationship Building */}
        {data["Relationship Building"] && (
          <div className="eval-section">
            <h3 className="eval-section-title">Relationship Building</h3>
            {Object.entries(data["Relationship Building"]).map(
              ([key, value]) => (
                <div key={key} className="eval-metric">
                  <span className="metric-label">{key}</span>
                  {isNumeric(value) ? (
                    renderScoreBar(value, 10)
                  ) : (
                    <p
                      style={{
                        marginTop: "5px",
                        color: "#555",
                        fontSize: "14px",
                      }}
                    >
                      {value}
                    </p>
                  )}
                </div>
              )
            )}
          </div>
        )}

        {/* Areas For Improvement */}
        {data["Areas for Improvement"] && (
          <div className="eval-section">
            <h3 className="eval-section-title">Areas For Improvement</h3>
            {Object.entries(data["Areas for Improvement"]).map(
              ([key, value]) => (
                <div key={key} className="eval-subsection">
                  <h4>{key}</h4>
                  {Array.isArray(value) ? (
                    <ul>
                      {value.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  ) : typeof value === "string" ? (
                    <p>{value}</p>
                  ) : (
                    <p>{JSON.stringify(value)}</p>
                  )}
                </div>
              )
            )}
          </div>
        )}
      </div>
    );
  };

  const renderClientEvaluation = (data) => {
    if (!data) return null;

    // Helper to render section content
    const renderSectionContent = (sectionData) => {
      if (!sectionData || typeof sectionData !== "object") return null;

      return Object.entries(sectionData).map(([key, value]) => (
        <div key={key} className="eval-text-item">
          <strong>{key}:</strong>
          <p>{typeof value === "object" ? JSON.stringify(value) : value}</p>
        </div>
      ));
    };

    return (
      <div className="evaluation-content">
        {/* Overall Client Score */}
        {data["Overall Client Score"] && (
          <div className="eval-section">
            <h3 className="eval-section-title">Overall Client Score</h3>
            {renderSectionContent(data["Overall Client Score"])}
          </div>
        )}

        {/* Client Profile */}
        {data["Client Profile"] && (
          <div className="eval-section">
            <h3 className="eval-section-title">Client Profile</h3>
            {renderSectionContent(data["Client Profile"])}
          </div>
        )}

        {/* Willingness & Readiness */}
        {data["Willingness & Readiness"] && (
          <div className="eval-section">
            <h3 className="eval-section-title">Willingness & Readiness</h3>
            {renderSectionContent(data["Willingness & Readiness"])}
          </div>
        )}

        {/* Interests & Engagement */}
        {data["Interests & Engagement"] && (
          <div className="eval-section">
            <h3 className="eval-section-title">Interests & Engagement</h3>
            {renderSectionContent(data["Interests & Engagement"])}
          </div>
        )}

        {/* Client Potential */}
        {data["Client Potential"] && (
          <div className="eval-section">
            <h3 className="eval-section-title">Client Potential</h3>
            {renderSectionContent(data["Client Potential"])}
          </div>
        )}

        {/* Opportunities */}
        {data["Opportunities"] && (
          <div className="eval-section">
            <h3 className="eval-section-title">Opportunities</h3>
            {renderSectionContent(data["Opportunities"])}
          </div>
        )}

        {/* Concerns & Objections */}
        {data["Concerns & Objections"] && (
          <div className="eval-section">
            <h3 className="eval-section-title">Concerns & Objections</h3>
            {renderSectionContent(data["Concerns & Objections"])}
          </div>
        )}

        {/* Recommended Next Steps */}
        {data["Recommended Next Steps"] && (
          <div className="eval-section">
            <h3 className="eval-section-title">Recommended Next Steps</h3>
            {renderSectionContent(data["Recommended Next Steps"])}
          </div>
        )}
      </div>
    );
  };

  const renderSentimentAnalysis = (data) => {
    if (!data) return null;

    return (
      <div style={{ color: "rgb(203, 213, 225)" }}>
        {/* Overall Sentiment with Emoji */}
        {data["Overall Sentiment"] &&
          typeof data["Overall Sentiment"] === "object" && (
            <div className="eval-section">
              <h3 className="eval-section-title">😊 Overall Sentiment</h3>
              {data["Overall Sentiment"]["Classification"] && (
                <div className="eval-text-item">
                  <strong>Classification:</strong>
                  <p
                    style={{
                      fontSize: "1.125rem",
                      fontWeight: "600",
                      color:
                        data["Overall Sentiment"]["Classification"] ===
                        "Positive"
                          ? "#27ae60"
                          : data["Overall Sentiment"]["Classification"] ===
                            "Negative"
                          ? "#e74c3c"
                          : "#f39c12",
                    }}
                  >
                    {data["Overall Sentiment"]["Classification"]}
                  </p>
                </div>
              )}
              {data["Overall Sentiment"]["Sentiment Score"] !== undefined && (
                <div className="eval-metric">
                  <span className="metric-label">Sentiment Score</span>
                  {renderScoreBar(
                    data["Overall Sentiment"]["Sentiment Score"],
                    10
                  )}
                </div>
              )}
              {data["Overall Sentiment"]["Confidence Score"] !== undefined && (
                <div className="eval-metric">
                  <span className="metric-label">Confidence Score</span>
                  {renderScoreBar(
                    data["Overall Sentiment"]["Confidence Score"],
                    100
                  )}
                </div>
              )}
            </div>
          )}

        {/* Sentiment Distribution */}
        {data["Sentiment Distribution"] &&
          typeof data["Sentiment Distribution"] === "object" && (
            <div className="eval-section">
              <h3 className="eval-section-title">🎭 Sentiment Distribution</h3>
              {Object.entries(data["Sentiment Distribution"]).map(
                ([emotion, value]) => (
                  <div key={emotion} className="eval-metric">
                    <span className="metric-label">{emotion}</span>
                    {renderScoreBar(value, 100)}
                  </div>
                )
              )}
            </div>
          )}

        {/* Engagement Level */}
        {data["Engagement Level"] &&
          typeof data["Engagement Level"] === "object" && (
            <div className="eval-section">
              <h3 className="eval-section-title">📊 Engagement Level</h3>
              {data["Engagement Level"]["Classification"] && (
                <div className="eval-text-item">
                  <strong>Classification:</strong>
                  <p
                    style={{
                      fontSize: "1.125rem",
                      fontWeight: "600",
                      color:
                        data["Engagement Level"]["Classification"] === "High"
                          ? "#27ae60"
                          : data["Engagement Level"]["Classification"] === "Low"
                          ? "#e74c3c"
                          : "#f39c12",
                    }}
                  >
                    {data["Engagement Level"]["Classification"]}
                  </p>
                </div>
              )}
              {data["Engagement Level"]["Indicators of engagement"] && (
                <div className="eval-text-item">
                  <strong>Indicators of engagement:</strong>
                  <p>{data["Engagement Level"]["Indicators of engagement"]}</p>
                </div>
              )}
            </div>
          )}

        {/* Sentiment Trajectory */}
        {data["Sentiment Trajectory"] &&
          typeof data["Sentiment Trajectory"] === "object" && (
            <div className="eval-section">
              <h3 className="eval-section-title">📈 Sentiment Trajectory</h3>
              {data["Sentiment Trajectory"]["Starting sentiment"] && (
                <div className="eval-text-item">
                  <strong>Starting Sentiment:</strong>
                  <p>{data["Sentiment Trajectory"]["Starting sentiment"]}</p>
                </div>
              )}
              {data["Sentiment Trajectory"]["Ending sentiment"] && (
                <div className="eval-text-item">
                  <strong>Ending Sentiment:</strong>
                  <p>{data["Sentiment Trajectory"]["Ending sentiment"]}</p>
                </div>
              )}
              {data["Sentiment Trajectory"]["Evolution"] && (
                <div className="eval-text-item">
                  <strong>Evolution:</strong>
                  <p>{data["Sentiment Trajectory"]["Evolution"]}</p>
                </div>
              )}
            </div>
          )}

        {/* Key Emotional Moments */}
        {data["Key Emotional Moments"] &&
          Array.isArray(data["Key Emotional Moments"]) &&
          data["Key Emotional Moments"].length > 0 && (
            <div className="eval-section">
              <h3 className="eval-section-title">⭐ Key Emotional Moments</h3>
              {data["Key Emotional Moments"].map((moment, idx) => {
                // Handle both capitalized and lowercase property names
                const emotion = moment.Emotion || moment.emotion || "";
                const quote = moment.Quote || moment.quote || "";
                const timestamp = moment.Timestamp || moment.timestamp || "";
                const significance =
                  moment.Significance || moment.significance || "";

                return (
                  <div
                    key={idx}
                    style={{
                      padding: "12px",
                      background: "rgba(59, 130, 246, 0.1)",
                      borderRadius: "8px",
                      borderLeft: "3px solid #3b82f6",
                      marginBottom: "12px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "8px",
                      }}
                    >
                      <strong style={{ color: "#60a5fa" }}>{emotion}</strong>
                      <span style={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                        {timestamp}
                      </span>
                    </div>
                    <p
                      style={{
                        fontStyle: "italic",
                        marginBottom: "8px",
                        color: "rgb(226, 232, 240)",
                      }}
                    >
                      "{quote}"
                    </p>
                    {significance && (
                      <p
                        style={{
                          fontSize: "0.875rem",
                          color: "rgb(148, 163, 184)",
                        }}
                      >
                        {significance}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

        {/* Positive Highlights */}
        {data["Positive Highlights"] &&
          typeof data["Positive Highlights"] === "object" && (
            <div className="eval-section">
              <h3 className="eval-section-title">✨ Positive Highlights</h3>
              {data["Positive Highlights"][
                "What resonated well with the client"
              ] && (
                <div className="eval-text-item">
                  <strong>What resonated well:</strong>
                  <p>
                    {
                      data["Positive Highlights"][
                        "What resonated well with the client"
                      ]
                    }
                  </p>
                </div>
              )}
              {data["Positive Highlights"][
                "Moments of strong positive sentiment"
              ] &&
                Array.isArray(
                  data["Positive Highlights"][
                    "Moments of strong positive sentiment"
                  ]
                ) && (
                  <div className="eval-text-item">
                    <strong>Moments of strong positive sentiment:</strong>
                    <ul style={{ paddingLeft: "20px", marginTop: "8px" }}>
                      {data["Positive Highlights"][
                        "Moments of strong positive sentiment"
                      ].map((moment, idx) => (
                        <li
                          key={idx}
                          style={{
                            marginBottom: "8px",
                            color: "#27ae60",
                            lineHeight: "1.6",
                          }}
                        >
                          {moment}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
            </div>
          )}

        {/* Red Flags */}
        {data["Red Flags"] && typeof data["Red Flags"] === "object" && (
          <div className="eval-section" style={{ borderLeftColor: "#e74c3c" }}>
            <h3 className="eval-section-title" style={{ color: "#e74c3c" }}>
              🚩 Red Flags
            </h3>
            {Object.entries(data["Red Flags"]).map(([key, value]) => (
              <div key={key} className="eval-text-item">
                <strong>{key}:</strong>
                <p style={{ color: "#e74c3c" }}>{value}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loadingSummary) {
    return (
      <div className="summary-page-container">
        <div className="error-content">
          <p style={{ color: "#667eea", fontSize: "1rem" }}>Loading meeting summary…</p>
        </div>
      </div>
    );
  }

  if (!summaryData) {
    return (
      <div className="summary-page-container">
        <div className="error-content">
          <h2>No Summary Data Found</h2>
          <p>The meeting summary is not available yet. It may still be processing.</p>
          <button onClick={() => navigate(-1)} className="back-btn">
            <ArrowLeft className="h-5 w-5" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const { summary, notes_content, action_items } = summaryData;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const copyAllContent = () => {
    const allContent = `
MEETING SUMMARY
===============

${summary}

MEETING NOTES
=============

${notes_content}

ACTION ITEMS
============

${action_items.replace(/<[^>]*>/g, "")} // Strip HTML tags for plain text copy
    `.trim();

    copyToClipboard(allContent);
  };

  const printSummary = () => {
    const printWindow = window.open('', '_blank');
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Meeting Summary - ${meetingId}</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
              line-height: 1.6;
              color: #333;
              max-width: 800px;
              margin: 40px auto;
              padding: 20px;
            }
            h1 {
              color: #667eea;
              border-bottom: 3px solid #667eea;
              padding-bottom: 10px;
              margin-bottom: 30px;
            }
            h2 {
              color: #764ba2;
              margin-top: 40px;
              margin-bottom: 20px;
              border-left: 4px solid #667eea;
              padding-left: 15px;
            }
            .section {
              margin-bottom: 40px;
              page-break-inside: avoid;
            }
            .suggestion-item {
              background: #f8f9fa;
              padding: 15px;
              margin: 15px 0;
              border-left: 4px solid #667eea;
              border-radius: 8px;
              page-break-inside: avoid;
            }
            .question {
              background: white;
              padding: 10px;
              margin-bottom: 10px;
              border-radius: 5px;
              font-style: italic;
            }
            a {
              color: #667eea;
              text-decoration: underline;
            }
            @media print {
              body {
                margin: 0;
                padding: 20px;
              }
            }
          </style>
        </head>
        <body>
          <h1>Meeting Summary</h1>
          
          <div class="section">
            <h2>Summary</h2>
            <p>${summary}</p>
          </div>

          ${notes_content ? `
            <div class="section">
              <h2>Meeting Notes</h2>
              <p>${notes_content}</p>
            </div>
          ` : ''}

          ${action_items ? `
            <div class="section">
              <h2>Action Items</h2>
              ${action_items}
            </div>
          ` : ''}

          ${suggestions.length > 0 ? `
            <div class="section">
              <h2>AI Suggestions (${suggestions.length})</h2>
              ${suggestions.map(s => `
                <div class="suggestion-item">
                  ${s.question ? `<div class="question"><strong>Question:</strong> ${s.question}</div>` : ''}
                  <p>${s.suggestion}</p>
                  ${s.timestamp ? `<small style="color: #666;">${new Date(s.timestamp).toLocaleString()}</small>` : ''}
                </div>
              `).join('')}
            </div>
          ` : ''}

          ${followUpQuestions.length > 0 ? `
            <div class="section">
              <h2>Follow-up Questions (${followUpQuestions.length})</h2>
              ${followUpQuestions.map(q => `
                <div class="suggestion-item">
                  <p>${q.question_text || q.question || q.text}</p>
                  ${q.previous_question ? `<div class="question"><strong>Context:</strong> ${q.previous_question}</div>` : ''}
                  ${q.created_at ? `<small style="color: #666;">${new Date(q.created_at).toLocaleString()}</small>` : ''}
                </div>
              `).join('')}
            </div>
          ` : ''}
        </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  // Parse action_items HTML (h3 owner + ul/li items) into a structured list.
  const parsedActionItems = (() => {
    if (!action_items) return [];
    try {
      const doc = new DOMParser().parseFromString(action_items, "text/html");
      const groups = [];
      let current = null;
      doc.body.childNodes.forEach((node) => {
        if (node.nodeName === "H3") {
          current = { owner: node.textContent.trim(), items: [] };
          groups.push(current);
        } else if (node.nodeName === "UL" && current) {
          node.querySelectorAll("li").forEach((li) => {
            current.items.push(li.textContent.trim());
          });
        }
      });
      if (groups.length === 0) {
        const text = doc.body.textContent.trim();
        if (text) {
          groups.push({
            owner: null,
            items: text
              .split("\n")
              .map((s) => s.trim())
              .filter(Boolean),
          });
        }
      }
      return groups;
    } catch {
      return [];
    }
  })();

  const totalActionItems = parsedActionItems.reduce(
    (n, g) => n + g.items.length,
    0
  );

  const toggleActionDone = (key) => {
    setCompletedActions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const dismissSuggestion = (id) => {
    setDismissedSuggestions((prev) => new Set(prev).add(id));
  };

  // Convert URLs in text to clickable links
  const linkifyText = (text) => {
    if (!text) return null;
    
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    
    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#667eea',
              textDecoration: 'underline',
              fontWeight: '600',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#764ba2';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#667eea';
            }}
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  return (
    <div className="summary-page-container">
      <style jsx>{`
        .summary-page-container {
          min-height: 100vh;
          background: #F5F3FF;
          padding: 20px;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
            sans-serif;
        }

        .summary-page-content {
          max-width: 1400px;
          margin: 0 auto;
        }

        .page-header {
          background: white;
          border: 2px solid #e9d5ff;
          border-radius: 16px;
          padding: 30px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
          box-shadow: 0 4px 20px rgba(102, 126, 234, 0.1);
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .back-btn {
          background: linear-gradient(135deg, #e04665 0%, #d94557 100%);
          border: none;
          border-radius: 10px;
          padding: 12px 16px;
          color: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
          font-weight: 500;
          box-shadow: 0 2px 8px rgba(224, 71, 101, 0.3);
        }

        .back-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(224, 71, 101, 0.4);
        }

        .page-title {
          font-size: 2rem;
          font-weight: 700;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 12px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .header-actions {
          display: flex;
          gap: 10px;
        }

        .action-btn {
          background: white;
          border: 2px solid #e9d5ff;
          border-radius: 10px;
          padding: 10px 16px;
          color: #667eea;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
          font-size: 14px;
          font-weight: 600;
          box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1);
        }

        .action-btn:hover {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-color: #667eea;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
        }

        .action-btn.evaluation-btn {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          border-color: #10b981;
        }

        .action-btn.evaluation-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
        }

        .action-btn.sentiment-btn {
          background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
          color: white;
          border-color: #8b5cf6;
        }

        .action-btn.sentiment-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
        }

        .action-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .summary-content {
          display: block;
        }

        .content-section {
          margin-bottom: 24px;
          padding: 32px;
          background: white;
          border-radius: 16px;
          border: 2px solid #e9d5ff;
          transition: all 0.3s ease;
          break-inside: avoid;
          box-shadow: 0 4px 20px rgba(102, 126, 234, 0.08);
        }

        .content-section:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(102, 126, 234, 0.15);
          border-color: #c4b5fd;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          padding-bottom: 16px;
          border-bottom: 2px solid #f3e8ff;
        }

        .section-title-group {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .section-title {
          font-size: 1.5rem;
          font-weight: 700;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin: 0;
        }

        .section-icon {
          width: 24px;
          height: 24px;
          color: #667eea;
        }

        .copy-section-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          border-radius: 8px;
          padding: 8px 14px;
          color: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          font-weight: 600;
          transition: all 0.3s ease;
          box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
        }

        .copy-section-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }

        .section-content {
          font-size: 1.05rem;
          line-height: 1.8;
          color: #4b5563;
          word-wrap: break-word;
          overflow-wrap: break-word;
          max-width: 100%;
        }

        .section-content p {
          margin-bottom: 16px;
          text-align: justify;
        }

        .action-items-content {
          font-size: 1.05rem;
          line-height: 1.8;
          color: #4b5563;
        }

        .action-items-content h3 {
          color: #667eea;
          font-size: 1.25rem;
          margin: 24px 0 12px 0;
          font-weight: 700;
        }

        .action-items-content ul {
          margin: 10px 0;
          padding-left: 20px;
        }

        .action-items-content li {
          margin: 8px 0;
          padding: 8px 0;
        }

        .error-content {
          text-align: center;
          padding: 80px 20px;
          background: white;
          border-radius: 16px;
          border: 2px solid #fecaca;
          box-shadow: 0 4px 20px rgba(239, 68, 68, 0.1);
        }

        .error-content h2 {
          font-size: 2rem;
          margin-bottom: 20px;
          color: #dc2626;
        }

        .error-content p {
          font-size: 1.1rem;
          margin-bottom: 30px;
          color: #6b7280;
        }

        .floating-actions {
          position: fixed;
          bottom: 30px;
          right: 30px;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .floating-btn {
          background: linear-gradient(135deg, #e04665 0%, #d94557 100%);
          border: none;
          border-radius: 50px;
          padding: 16px 24px;
          color: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 600;
          box-shadow: 0 4px 20px rgba(224, 71, 101, 0.4);
          transition: all 0.3s ease;
        }

        .floating-btn:hover {
          transform: translateY(-3px) scale(1.05);
          box-shadow: 0 6px 30px rgba(224, 71, 101, 0.5);
        }

        /* Evaluation Modal Styles */
        .evaluation-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.75);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .evaluation-modal {
          background: white;
          border-radius: 16px;
          max-width: 900px;
          width: 100%;
          max-height: 90vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .modal-header {
          background: white;
          border-bottom: 2px solid #e9d5ff;
          color: #667eea;
          padding: 25px 30px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-title {
          font-size: 1.5rem;
          font-weight: 700;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 12px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .close-modal-btn {
          background: #f3e8ff;
          border: none;
          border-radius: 50%;
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #667eea;
          transition: all 0.3s ease;
        }

        .close-modal-btn:hover {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          transform: rotate(90deg);
        }

        .modal-tabs {
          display: flex;
          border-bottom: 2px solid #e9ecef;
          background: #f8f9fa;
          padding: 0 30px;
        }

        .tab-btn {
          flex: 1;
          padding: 15px 20px;
          border: none;
          background: transparent;
          color: #666;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          position: relative;
          transition: all 0.3s ease;
        }

        .tab-btn:hover {
          color: #667eea;
        }

        .tab-btn.active {
          color: #667eea;
        }

        .tab-btn.active::after {
          content: "";
          position: absolute;
          bottom: -2px;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }

        .modal-content {
          flex: 1;
          overflow-y: auto;
          padding: 30px;
        }

        .evaluation-content {
          display: flex;
          flex-direction: column;
          gap: 25px;
        }

        .eval-section {
          background: #f8f9fa;
          padding: 20px;
          border-radius: 12px;
          border-left: 4px solid #667eea;
        }

        .eval-section-title {
          font-size: 1.3rem;
          font-weight: 600;
          color: #2c3e50;
          margin: 0 0 15px 0;
        }

        .eval-metric {
          margin-bottom: 20px;
        }

        .metric-label {
          font-size: 15px;
          font-weight: 500;
          color: #555;
          display: block;
          margin-bottom: 5px;
        }

        .eval-text-item {
          margin-bottom: 15px;
        }

        .eval-text-item strong {
          color: #2c3e50;
          font-weight: 600;
          display: block;
          margin-bottom: 5px;
        }

        .eval-text-item p {
          color: #555;
          line-height: 1.6;
          margin: 0;
        }

        .eval-subsection {
          margin-top: 15px;
        }

        .eval-subsection h4 {
          color: #2c3e50;
          font-size: 1.1rem;
          font-weight: 600;
          margin: 0 0 10px 0;
        }

        .eval-subsection ul {
          margin: 0;
          padding-left: 20px;
        }

        .eval-subsection li {
          color: #555;
          line-height: 1.6;
          margin-bottom: 8px;
        }

        .loading-spinner {
          text-align: center;
          padding: 40px;
        }

        .spinner {
          border: 4px solid #f3f3f3;
          border-top: 4px solid #667eea;
          border-radius: 50%;
          width: 50px;
          height: 50px;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .error-message {
          background: #fee;
          border-left: 4px solid #e74c3c;
          padding: 15px;
          border-radius: 8px;
          color: #c0392b;
          margin: 20px 0;
        }

        @media (max-width: 768px) {
          .summary-page-container {
            padding: 10px;
          }

          .page-header {
            padding: 20px;
            flex-direction: column;
            gap: 15px;
          }

          .header-left {
            width: 100%;
            justify-content: space-between;
          }

          .page-title {
            font-size: 1.5rem;
          }

          .summary-content {
            padding: 20px;
          }

          .content-section {
            padding: 20px;
            margin-bottom: 25px;
          }

          .section-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }

          .floating-actions {
            bottom: 20px;
            right: 20px;
          }

          .evaluation-modal {
            max-height: 95vh;
          }

          .modal-content {
            padding: 20px;
          }
        }

        /* Suggestions Styles */
        .suggestions-count {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 6px 16px;
          border-radius: 20px;
          font-size: 0.85em;
          font-weight: 700;
          box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
        }

        .suggestions-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .suggestion-card {
          background: linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%);
          padding: 24px;
          border-radius: 16px;
          border: 2px solid #e9d5ff;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.08);
          transition: all 0.3s ease;
        }

        .suggestion-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(102, 126, 234, 0.15);
          border-color: #c4b5fd;
        }

        .suggestion-question {
          background: white;
          padding: 12px 16px;
          border-radius: 10px;
          margin-bottom: 14px;
          border-left: 4px solid #667eea;
          font-size: 0.95em;
          color: #4b5563;
          box-shadow: 0 2px 6px rgba(102, 126, 234, 0.1);
        }

        .suggestion-text {
          font-size: 1rem;
          line-height: 1.7;
          color: #374151;
          margin-bottom: 14px;
          white-space: pre-wrap;
          font-weight: 500;
        }

        .suggestion-timestamp {
          font-size: 0.85em;
          color: #666;
          margin-bottom: 12px;
        }

        .suggestion-feedback-section {
          margin-top: 16px;
          padding-top: 16px;
          border-top: 1px solid rgba(0, 0, 0, 0.1);
        }

        .feedback-rating {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 8px;
        }

        .rating-label {
          font-size: 0.85em;
          color: #666;
          font-weight: 600;
        }

        .stars {
          display: flex;
          gap: 4px;
        }

        .feedback-submitted-badge {
          font-size: 0.8em;
          color: #10b981;
          font-weight: 600;
          margin-left: 8px;
        }

        .feedback-input-section {
          margin-top: 12px;
          animation: fadeIn 0.3s ease;
        }

        .feedback-textarea {
          width: 100%;
          min-height: 80px;
          padding: 10px 12px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 0.9em;
          font-family: inherit;
          resize: vertical;
          outline: none;
          transition: border-color 0.2s ease;
        }

        .feedback-textarea:focus {
          border-color: #667eea;
        }

        .submit-feedback-btn {
          margin-top: 8px;
          padding: 8px 16px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 0.85em;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s ease;
        }

        .submit-feedback-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }

        .submit-feedback-btn:disabled {
          background: #9ca3af;
          cursor: not-allowed;
        }

        .existing-feedback {
          font-size: 0.9em;
          color: #555;
        }

        .feedback-rating-display {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
        }

        .stars-display {
          display: flex;
          gap: 2px;
        }

        .feedback-text-display {
          margin-top: 8px;
          padding: 10px;
          background: rgba(255, 255, 255, 0.7);
          border-radius: 6px;
        }

        .feedback-timestamp {
          font-size: 0.8em;
          color: #999;
          margin-top: 6px;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      <div className="max-w-7xl mx-auto">
        {/* Top bar */}
        <div className="bg-white border border-gray-200 rounded-xl px-5 py-4 mb-4">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-3 min-w-0">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="p-2 -ml-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                title="Back"
                aria-label="Back"
              >
                <ArrowLeft size={18} />
              </button>
              <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2 truncate">
                <FileText size={20} className="text-gray-700 flex-shrink-0" />
                Meeting Summary
              </h1>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={fetchEvaluation}
                disabled={loadingEvaluation}
                className="px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 transition-colors flex items-center gap-2"
                title="Sales/client evaluation"
              >
                <BarChart3 size={14} />
                {loadingEvaluation ? "Loading…" : "Evaluate"}
              </button>
              <button
                type="button"
                onClick={fetchSentiment}
                disabled={loadingSentiment}
                className="px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 transition-colors flex items-center gap-2"
                title="Conversation sentiment"
              >
                <SmilePlus size={14} />
                {loadingSentiment ? "Loading…" : "Sentiment"}
              </button>

              {/* Export menu */}
              <div className="relative" ref={exportMenuRef}>
                <button
                  type="button"
                  onClick={() => setShowExportMenu((v) => !v)}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium text-white transition-colors flex items-center gap-2"
                  style={{ backgroundColor: "#e04665" }}
                >
                  <Download size={14} />
                  Export
                  <ChevronDown
                    size={14}
                    className={`transition-transform ${showExportMenu ? "rotate-180" : ""}`}
                  />
                </button>
                {showExportMenu && (
                  <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-20">
                    <button
                      type="button"
                      onClick={() => {
                        copyAllContent();
                        setShowExportMenu(false);
                      }}
                      className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <Copy size={14} />
                      Copy all
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        printSummary();
                        setShowExportMenu(false);
                      }}
                      className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <Printer size={14} />
                      Print / save as PDF
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.href);
                        setShowExportMenu(false);
                      }}
                      className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <Hash size={14} />
                      Copy page link
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Meta strip */}
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="font-mono text-gray-600 bg-gray-100 px-2 py-1 rounded-full">
              ID: {meetingId}
            </span>
            <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-700 flex items-center gap-1">
              <CheckCircle size={12} /> {totalActionItems} action item
              {totalActionItems !== 1 ? "s" : ""}
            </span>
            <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-700 flex items-center gap-1">
              <Lightbulb size={12} /> {suggestions.length} suggestion
              {suggestions.length !== 1 ? "s" : ""}
            </span>
            <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-700 flex items-center gap-1">
              <HelpCircle size={12} /> {followUpQuestions.length} follow-up
              {followUpQuestions.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>

        {(evaluationError || sentimentError) && (
          <div className="mb-4 space-y-2">
            {evaluationError && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {evaluationError}
              </div>
            )}
            {sentimentError && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {sentimentError}
              </div>
            )}
          </div>
        )}

        {/* Main grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <div className="lg:col-span-3 space-y-4">
            {/* Summary */}
            <SummarySection
              id="summary"
              icon={FileText}
              title="Summary"
              onCopy={() => copyToClipboard(summary)}
            >
              <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                {linkifyText(summary)}
              </p>
            </SummarySection>

            {/* Notes */}
            {notes_content && (
              <SummarySection
                id="notes"
                icon={Users}
                title="Meeting Notes"
                onCopy={() => copyToClipboard(notes_content)}
              >
                <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                  {linkifyText(notes_content)}
                </p>
              </SummarySection>
            )}

            {/* Action Items */}
            {parsedActionItems.length > 0 && (
              <SummarySection
                id="action-items"
                icon={CheckCircle}
                title="Action Items"
                badge={
                  totalActionItems > 0
                    ? `${completedActions.size}/${totalActionItems} done`
                    : null
                }
                onCopy={() =>
                  copyToClipboard(action_items.replace(/<[^>]*>/g, ""))
                }
              >
                <div className="space-y-4">
                  {parsedActionItems.map((group, gi) => (
                    <div key={`g-${gi}`}>
                      {group.owner && (
                        <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
                          {group.owner}
                        </div>
                      )}
                      <ul className="space-y-1.5">
                        {group.items.map((item, ii) => {
                          const key = `${gi}-${ii}`;
                          const done = completedActions.has(key);
                          return (
                            <li key={key} className="flex items-start gap-2.5">
                              <button
                                type="button"
                                onClick={() => toggleActionDone(key)}
                                className={`mt-0.5 w-4 h-4 flex-shrink-0 rounded border flex items-center justify-center transition-colors ${
                                  done
                                    ? "border-green-500 bg-green-500 text-white"
                                    : "border-gray-300 hover:border-gray-500"
                                }`}
                                aria-label={done ? "Mark not done" : "Mark done"}
                              >
                                {done && <Check size={12} strokeWidth={3} />}
                              </button>
                              <span
                                className={`text-gray-800 leading-relaxed ${
                                  done ? "line-through opacity-60" : ""
                                }`}
                              >
                                {linkifyText(item)}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </div>
              </SummarySection>
            )}

            {/* AI Suggestions */}
            {suggestions.length > 0 && (
              <SummarySection
                id="suggestions"
                icon={Lightbulb}
                title="AI Suggestions"
                badge={`${suggestions.length}`}
              >
                {loadingSuggestions ? (
                  <div className="text-sm text-gray-500 italic">Loading…</div>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {suggestions
                      .filter((s) => !dismissedSuggestions.has(s.id))
                      .map((suggestion) => {
                        const text = suggestion.suggestion || "";
                        const fbState = feedbackStates[suggestion.id] || {};
                        const alreadyRated =
                          suggestion.rating || suggestion.feedback;
                        const draftRating = fbState.rating || 0;
                        const showFeedbackBox = fbState.showFeedbackBox;
                        const submitted = fbState.submitted;
                        const submitting = submittingFeedback[suggestion.id];
                        return (
                          <li
                            key={suggestion.id}
                            className="group relative py-3"
                          >
                            <div className="absolute top-2 right-0 flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                              <button
                                type="button"
                                onClick={() => copyToClipboard(text)}
                                className="p-1 text-gray-400 hover:text-gray-700 rounded"
                                title="Copy"
                                aria-label="Copy"
                              >
                                <Copy size={14} />
                              </button>
                              <button
                                type="button"
                                onClick={() => dismissSuggestion(suggestion.id)}
                                className="p-1 text-gray-400 hover:text-red-600 rounded"
                                title="Dismiss"
                                aria-label="Dismiss"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                            <div className="pr-16">
                              {suggestion.question && (
                                <div className="text-xs text-gray-500 italic mb-1">
                                  Context: {linkifyText(suggestion.question)}
                                </div>
                              )}
                              <div className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                                {linkifyText(text)}
                              </div>
                              {suggestion.timestamp && (
                                <div className="text-xs text-gray-400 mt-1">
                                  {new Date(suggestion.timestamp).toLocaleString()}
                                </div>
                              )}

                              {/* Feedback */}
                              <div className="mt-3 pt-3 border-t border-gray-100">
                                {alreadyRated ? (
                                  <div className="space-y-1 text-xs text-gray-600">
                                    {suggestion.rating && (
                                      <div className="flex items-center gap-2">
                                        <span>Your rating:</span>
                                        <div className="flex">
                                          {[1, 2, 3, 4, 5].map((star) => (
                                            <Star
                                              key={star}
                                              size={14}
                                              fill={
                                                star <= suggestion.rating
                                                  ? "#fbbf24"
                                                  : "none"
                                              }
                                              stroke={
                                                star <= suggestion.rating
                                                  ? "#fbbf24"
                                                  : "#d1d5db"
                                              }
                                            />
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    {suggestion.feedback && (
                                      <div>
                                        <span className="font-medium text-gray-700">
                                          Your feedback:
                                        </span>{" "}
                                        {suggestion.feedback}
                                      </div>
                                    )}
                                    {suggestion.feedback_timestamp && (
                                      <div className="text-gray-400">
                                        Submitted{" "}
                                        {new Date(
                                          suggestion.feedback_timestamp
                                        ).toLocaleString()}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-xs text-gray-600">
                                        Rate this suggestion:
                                      </span>
                                      <div className="flex gap-0.5">
                                        {[1, 2, 3, 4, 5].map((star) => {
                                          const selected = star <= draftRating;
                                          return (
                                            <button
                                              key={star}
                                              type="button"
                                              onClick={() =>
                                                handleStarClick(
                                                  suggestion.id,
                                                  star
                                                )
                                              }
                                              className="hover:scale-110 transition-transform"
                                              aria-label={`Rate ${star} star${star === 1 ? "" : "s"}`}
                                            >
                                              <Star
                                                size={16}
                                                fill={
                                                  selected ? "#fbbf24" : "none"
                                                }
                                                stroke={
                                                  selected
                                                    ? "#fbbf24"
                                                    : "#9ca3af"
                                                }
                                              />
                                            </button>
                                          );
                                        })}
                                      </div>
                                      {submitted && (
                                        <span className="text-xs text-green-600 font-medium">
                                          ✓ Feedback submitted
                                        </span>
                                      )}
                                    </div>
                                    {showFeedbackBox && (
                                      <div className="mt-2">
                                        <textarea
                                          placeholder="Share your thoughts about this suggestion… (optional)"
                                          value={fbState.feedback || ""}
                                          onChange={(e) =>
                                            handleFeedbackChange(
                                              suggestion.id,
                                              e.target.value
                                            )
                                          }
                                          className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                                          rows={3}
                                        />
                                        <button
                                          type="button"
                                          onClick={() =>
                                            submitFeedback(
                                              suggestion.id,
                                              draftRating,
                                              fbState.feedback || ""
                                            )
                                          }
                                          disabled={submitting}
                                          className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-white rounded-lg disabled:opacity-50 transition-colors"
                                          style={{
                                            backgroundColor: "#e04665",
                                          }}
                                        >
                                          <Send size={12} />
                                          {submitting
                                            ? "Submitting…"
                                            : "Submit Feedback"}
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </li>
                        );
                      })}
                  </ul>
                )}
              </SummarySection>
            )}

            {/* Follow-up Questions */}
            {followUpQuestions.length > 0 && (
              <SummarySection
                id="questions"
                icon={HelpCircle}
                title="Follow-up Questions"
                badge={`${followUpQuestions.length}`}
              >
                {loadingFollowUpQuestions ? (
                  <div className="text-sm text-gray-500 italic">Loading…</div>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {followUpQuestions.map((q, i) => {
                      const text = q.question_text || q.question || q.text || "";
                      return (
                        <li
                          key={q.id || i}
                          className="group relative py-3"
                        >
                          <button
                            type="button"
                            onClick={() => copyToClipboard(text)}
                            className="absolute top-2 right-0 p-1 text-gray-400 hover:text-gray-700 opacity-60 group-hover:opacity-100 transition-opacity"
                            title="Copy"
                            aria-label="Copy"
                          >
                            <Copy size={14} />
                          </button>
                          <div className="text-gray-800 leading-relaxed pr-8">
                            {linkifyText(text)}
                          </div>
                          {q.previous_question && (
                            <div className="text-xs text-gray-500 italic mt-1">
                              Context: "{q.previous_question}"
                            </div>
                          )}
                          {(q.category || q.created_at) && (
                            <div className="text-xs text-gray-400 mt-1 flex items-center gap-2">
                              {q.category && <span>{q.category}</span>}
                              {q.created_at && (
                                <span>
                                  {new Date(q.created_at).toLocaleString()}
                                </span>
                              )}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </SummarySection>
            )}
          </div>

          {/* Right rail TOC (lg+ only) */}
          <aside className="hidden lg:block">
            <nav className="sticky top-4 bg-white border border-gray-200 rounded-xl p-4 text-sm">
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-2">
                <List size={12} />
                On this page
              </div>
              <ul className="space-y-1">
                <TocLink href="summary" icon={FileText} label="Summary" />
                {notes_content && (
                  <TocLink href="notes" icon={Users} label="Notes" />
                )}
                {parsedActionItems.length > 0 && (
                  <TocLink
                    href="action-items"
                    icon={CheckCircle}
                    label="Action Items"
                    count={totalActionItems}
                  />
                )}
                {suggestions.length > 0 && (
                  <TocLink
                    href="suggestions"
                    icon={Lightbulb}
                    label="Suggestions"
                    count={
                      suggestions.length - dismissedSuggestions.size
                    }
                  />
                )}
                {followUpQuestions.length > 0 && (
                  <TocLink
                    href="questions"
                    icon={HelpCircle}
                    label="Follow-ups"
                    count={followUpQuestions.length}
                  />
                )}
              </ul>
            </nav>
          </aside>
        </div>
      </div>

      {/* Evaluation Modal */}
      {showEvaluationModal && evaluationData && (
        <div
          className="evaluation-modal-overlay"
          onClick={() => setShowEvaluationModal(false)}
        >
          <div
            className="evaluation-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2 className="modal-title">
                <BarChart3 className="h-6 w-6" />
                Meeting Evaluation
              </h2>
              <button
                className="close-modal-btn"
                onClick={() => setShowEvaluationModal(false)}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="modal-tabs">
              <button
                className={`tab-btn ${
                  evaluationType === "sales" ? "active" : ""
                }`}
                onClick={() => setEvaluationType("sales")}
              >
                Sales Evaluation
              </button>
              <button
                className={`tab-btn ${
                  evaluationType === "client" ? "active" : ""
                }`}
                onClick={() => setEvaluationType("client")}
              >
                Client Evaluation
              </button>
            </div>

            <div className="modal-content">
              {loadingEvaluation ? (
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <p>Loading evaluation data...</p>
                </div>
              ) : evaluationType === "sales" ? (
                renderSalesEvaluation(evaluationData?.sales_evaluation)
              ) : (
                renderClientEvaluation(evaluationData?.client_evaluation)
              )}
            </div>
          </div>
        </div>
      )}

      {/* Sentiment Modal */}
      {showSentimentModal && sentimentData && (
        <div
          className="evaluation-modal-overlay"
          onClick={() => setShowSentimentModal(false)}
        >
          <div
            className="evaluation-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2 className="modal-title">
                <SmilePlus className="h-6 w-6" />
                Customer Sentiment Analysis
              </h2>
              <button
                className="close-modal-btn"
                onClick={() => setShowSentimentModal(false)}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="modal-content">
              {loadingSentiment ? (
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <p>Loading sentiment analysis...</p>
                </div>
              ) : sentimentError ? (
                <div className="error-message">{sentimentError}</div>
              ) : (
                renderSentimentAnalysis(sentimentData)
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeetingSummaryPage;
